import { useState, useEffect, useCallback } from 'react';
import type { Word } from '../../types/word.types';
import { PageContainer } from '../../components/PageContainer';
import { userWordsService } from '../../services/userWordsService';
import type { WordSearchData, SearchedWordData } from '../../services/wordSearchService';
import { Button } from '../../components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';
import { Loading } from '../../components/ui/loading';
import { ActiveFilterPills, type FilterPill } from '../../components/ActiveFilterPills';
import { AlertCircle } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { LayoutGrid, List, MoreVertical, BookOpen } from 'lucide-react';
import { loadViewMode, saveViewMode } from '../../utils/localStorage';
import WordInput from '../../components/LearnWords/WordInput';
import WordList from '../../components/LearnWords/WordList';
import { WordFilterPopover, type WordFilters } from '../../components/WordFilterPopover';

type ViewMode = 'card' | 'list';

interface ViewToggleProps {
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
}

function ViewToggle({ viewMode, onViewModeChange }: ViewToggleProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          {viewMode === 'card' ? (
            <LayoutGrid className="h-4 w-4" />
          ) : (
            <List className="h-4 w-4" />
          )}
          {viewMode === 'card' ? 'Card' : 'List'}
          <MoreVertical className="h-4 w-4 ml-1" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem
          onClick={() => onViewModeChange('card')}
          className={`gap-2 ${viewMode === 'card' ? 'bg-accent' : ''}`}
        >
          <LayoutGrid className="h-4 w-4" />
          Card View
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => onViewModeChange('list')}
          className={`gap-2 ${viewMode === 'list' ? 'bg-accent' : ''}`}
        >
          <List className="h-4 w-4" />
          List View
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function LearnWordsPage() {
  const [words, setWords] = useState<Word[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const saved = loadViewMode();
    return saved || 'card';
  });
  const [filters, setFilters] = useState<WordFilters>({
    confidenceLevels: [],
    sortBy: 'newest',
  });

  // Fetch user's words on mount
  useEffect(() => {
    const fetchWords = async () => {
      try {
        setInitialLoading(true);
        const userWords = await userWordsService.getUserWords();
        setWords(userWords);
      } catch (err: unknown) {
        console.error('Error fetching words:', err);
        const errorMessage = err instanceof Error ? err.message : 'Failed to load words. Please refresh the page.';
        setError(errorMessage);
      } finally {
        setInitialLoading(false);
      }
    };

    fetchWords();
  }, []);

  const handleAddWord = async (word: string, searchData?: WordSearchData) => {
    const trimmedWord = word.trim();
    if (!trimmedWord) {
      console.warn('Empty word');
      return;
    }

    try {
      setError(null); // Clear any previous errors
      // Add word to backend
      const response = await userWordsService.addWord(trimmedWord);

      // Format word: capitalize first letter, rest lowercase
      const formattedWord = trimmedWord.charAt(0).toUpperCase() + trimmedWord.slice(1).toLowerCase();

      // Convert search data to definitions if available
      const definitions = searchData ? searchData.data.flatMap((entry: SearchedWordData) =>
        entry.meanings.map((meaning) => ({
          partOfSpeech: meaning.partOfSpeech,
          meaning: meaning.definitions[0]?.definition || '',
          example: meaning.definitions[0]?.example,
          synonyms: meaning.synonyms,
          antonyms: meaning.antonyms,
          definitions: meaning.definitions,
        }))
      ) : [];

      // The response contains the newly created word data
      // Create a Word object from the response
      const newWord: Word = {
        id: response.data.id,
        word: formattedWord,
        definitions,
        addedAt: new Date(response.data.created_at),
        rating: 1,
        phonetic: searchData?.data[0]?.phonetic,
        phonetics: searchData?.data[0]?.phonetics,
        sourceUrls: searchData?.data[0]?.sourceUrls,
      };

      // Add to the beginning of both words and filteredWords lists
      setWords((prevWords) => [newWord, ...prevWords]);
      setFilteredWords((prevFiltered) => [newWord, ...prevFiltered]);
    } catch (err: unknown) {
      console.error('Error adding word:', err);

      // Extract user-friendly error message
      const errorMessage = err instanceof Error ? err.message :
        typeof err === 'object' && err !== null && 'message' in err ?
        String((err as { message: unknown }).message) :
        'Failed to add word. Please try again.';

      setError(errorMessage);
    }
  };

  const handleDeleteWord = async (wordId: string) => {
    try {
      const word = words.find((w) => w.id === wordId);
      if (!word) return;

      await userWordsService.deleteWord(word.word);

      // Update both full words list and filtered words list
      setWords((prevWords) => prevWords.filter((w) => w.id !== wordId));
      setFilteredWords((prevFiltered) => prevFiltered.filter((w) => w.id !== wordId));
    } catch (err: unknown) {
      console.error('Error deleting word:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete word. Please try again.';
      setError(errorMessage);
    }
  };

  const handleRatingChange = async (wordId: string, rating: number) => {
    const word = words.find((w) => w.id === wordId);
    if (!word) return;

    // Don't make API call if rating hasn't changed
    const currentRating = word.rating || 1;
    if (currentRating === rating) {
      return;
    }

    // Store the original rating for potential rollback
    const originalRating = currentRating;

    // Optimistically update the UI immediately
    setWords((prevWords) =>
      prevWords.map((w) =>
        w.id === wordId ? { ...w, rating } : w
      )
    );

    // Also update filtered words optimistically
    setFilteredWords((prevFiltered) =>
      prevFiltered.map((w) =>
        w.id === wordId ? { ...w, rating } : w
      )
    );

    try {
      await userWordsService.updateWord(word.word, { rating });
      // API call succeeded - no need to do anything else, UI is already updated
    } catch (err: unknown) {
      console.error('Error updating rating:', err);

      // Revert the optimistic updates on error
      setWords((prevWords) =>
        prevWords.map((w) =>
          w.id === wordId ? { ...w, rating: originalRating } : w
        )
      );

      setFilteredWords((prevFiltered) =>
        prevFiltered.map((w) =>
          w.id === wordId ? { ...w, rating: originalRating } : w
        )
      );

      const errorMessage = err instanceof Error ? err.message : 'Failed to update word rating. Please try again.';
      setError(errorMessage);
    }
  };


  const [filteredWords, setFilteredWords] = useState<Word[]>([]);
  const [filterLoading, setFilterLoading] = useState(false);

  // Function to apply filters and fetch results
  const applyFiltersAndFetch = useCallback(async () => {
    setFilterLoading(true);
    try {
      const searchParams = {
        ratings: filters.confidenceLevels.length > 0 ? filters.confidenceLevels : undefined,
        sortBy: filters.sortBy,
      };

      const results = await userWordsService.searchWords(searchParams);
      setFilteredWords(results);
    } catch (err: unknown) {
      console.error('Error applying filters:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to filter words. Please try again.';
      setError(errorMessage);
    } finally {
      setFilterLoading(false);
    }
  }, [filters]);

  // Function to get active filter pills for display
  const getActiveFilterPills = (): FilterPill[] => {
    const pills: FilterPill[] = [];

    // Confidence level filters
    [...filters.confidenceLevels].sort((a, b) => a - b).forEach(level => {
      pills.push({
        key: `confidence-${level}`,
        label: `${level}★`,
        remove: () => setFilters(prev => ({
          ...prev,
          confidenceLevels: prev.confidenceLevels.filter(l => l !== level)
        }))
      });
    });

    // Sort by filter
    if (filters.sortBy !== 'newest') {
      const sortLabels = {
        oldest: 'Oldest First',
        alphabetical: 'Alphabetical'
      };
      pills.push({
        key: 'sortBy',
        label: `Sort: ${sortLabels[filters.sortBy]}`,
        remove: () => setFilters(prev => ({ ...prev, sortBy: 'newest' }))
      });
    }

    return pills;
  };

  // Apply filters and sorting via backend
  useEffect(() => {
    applyFiltersAndFetch();
  }, [filters, applyFiltersAndFetch]);

  // Save view mode when it changes
  useEffect(() => {
    saveViewMode(viewMode);
  }, [viewMode]);

  const totalCount = words.length;


  if (initialLoading) {
    return (
      <PageContainer>
        <Loading size="lg" message="Loading your words..." />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="space-y-4 md:space-y-6">

        <div className="space-y-3">
          <WordInput onAddWord={handleAddWord} />


        {/* Error Message */}
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Failed to add word</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
            <Button
              onClick={() => setError(null)}
              variant="ghost"
              size="sm"
              className="absolute top-2 right-2 h-6 w-6 p-0"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </Button>
          </Alert>
        )}

        {totalCount > 0 && (
          <div>
            <div className="flex items-center justify-between pb-2">
              <div className="flex items-center">
                <div className="flex items-center px-2 py-1 rounded-md bg-muted/50">
                  <BookOpen className="h-4 w-4 text-muted-foreground mr-2" />
                  <span className="font-medium text-foreground text-base mr-1">
                    {filteredWords.length}
                  </span>
                  <span className="text-muted-foreground text-sm">
                    {filteredWords.length === 1 ? 'word' : 'words'}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <WordFilterPopover
                  filters={filters}
                  onFiltersChange={(newFilters) => {
                    setFilters(newFilters);
                    applyFiltersAndFetch();
                  }}
                />
                <ViewToggle
                  viewMode={viewMode}
                  onViewModeChange={setViewMode}
                />
              </div>
            </div>
            <ActiveFilterPills pills={getActiveFilterPills()} />
            {filterLoading ? (
              <Loading size="lg" message="Applying filters..." />
            ) : (
              <WordList
                words={filteredWords}
                onDeleteWord={handleDeleteWord}
                onRatingChange={handleRatingChange}
                totalWordsCount={totalCount}
                viewMode={viewMode}
              />
            )}
          </div>
        )}
        </div>
      </div>
    </PageContainer>
  );
}

export default LearnWordsPage;
